import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { FieldValues, Path, UseFormRegisterReturn, UseFormReturn, useWatch } from "react-hook-form";

import { getCategories, groupByParent } from "../../core/category.core";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import UpIcon from "../../icons/up.icon";
import { Category } from "../../types/category";
import { FancyRadiobox } from "../reuseables/form.comp";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";

export default function SingleCategorySelectorComp<T extends FieldValues>({
  methods,
  name,
  labelMaxLength = 60,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength?: number;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  function onClick(e: MouseEvent) {
    if (!ref.current) return;
    if (ref.current.getAttribute("data-state") !== "true") return;
    const isWithin = (e.target as HTMLElement).closest("#select-single-category");
    if (!isWithin) {
      setIsOpened(false);
    }
  }

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  return (
    <div id="select-single-category" ref={ref} data-state={isOpened} className={css}>
      <button
        type="button"
        className="w-full h-full flex justify-between pr-2 pl-4 items-center"
        onClick={() => setIsOpened((v) => !v)}
      >
        <CurrentName methods={methods} name={name} labelMaxLength={labelMaxLength} /> <ChevronDownIcon />
      </button>
      {isOpened && <Panel methods={methods} name={name} closePanel={() => setIsOpened(false)} />}
    </div>
  );
}

function CurrentName<T extends FieldValues>({
  methods,
  name,
  labelMaxLength,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  labelMaxLength: number;
}) {
  const catId = useWatch({ name, control: methods.control }) as string | undefined;
  const { msg, t } = useTranslation();
  const { data: categories, isFetched } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { locale, isReady } = useRouter();

  if (!isFetched || !categories || !isReady) return null;

  if (catId === undefined || catId === "") {
    return <div>{t(msg.menuItem.selectCategory)}</div>;
  }

  const foundCat = categories.find((c) => c.id === catId);
  if (!foundCat) return <div>{t(msg.menuItem.selectCategory)}</div>;

  let text = localeCore.getLocaleValueFallback(foundCat.name, locale!) || foundCat.code;
  if (text.length > labelMaxLength) {
    text = text.substring(0, labelMaxLength - 3) + " ...";
  }

  return <div>{text}</div>;
}

function Panel<T extends FieldValues>({
  methods,
  name,
  closePanel,
}: {
  methods: UseFormReturn<T, any>;
  name: Path<T>;
  closePanel: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const { t, msg } = useTranslation();
  const {
    data: categories,
    isError,
    isPending,
    error,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  const registerField = methods.register(name, {
    onChange: () => closePanel(),
  });
  const selectedId = useWatch({ control: methods.control, name }) as string | undefined;

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isPending) return <LoadingOvalCenterIcon />;
  if (!categories) return null;

  const grouped = groupByParent([...categories]);
  const filteredRoots = filterCategoryTree(grouped, searchTerm);

  return (
    <div className="category_panel absolute top-[34px] left-0 w-full border rounded-b-md hover:border-blue-200 bg-white dark:bg-her-backblue z-10 max-h-80 flex flex-col shadow-sm">
      <div className="px-3 pt-3 pb-2 shrink-0 border-b border-gray-100 dark:border-slate-600">
        <input
          type="search"
          placeholder={t(msg.category.search)}
          className="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-her-backblue dark:border-white text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="overflow-y-auto px-4 py-3 space-y-1 max-h-60">
        <FancyRadiobox
          value=""
          register={registerField}
          labelId="single-cat-none"
          defaultChecked={selectedId === undefined || selectedId === ""}
        >
          {t(msg.menuItem.selectCategory)}
        </FancyRadiobox>
        {filteredRoots.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">{t(msg.general.noRecordFound)}</div>
        ) : (
          filteredRoots.map((c) => (
            <TreeRow key={c.id} cat={c} level={0} registerField={registerField} selectedId={selectedId} />
          ))
        )}
      </div>
    </div>
  );
}

function filterCategoryTree(cats: Category[], searchTerm: string): Category[] {
  if (!searchTerm) return cats;

  const matchesSearch = (cat: Category): boolean => {
    const name = localeCore.getLocaleValueDefaultFallback(cat.name)?.toLowerCase() || "";
    const code = (cat.code || "").toLowerCase();
    const q = searchTerm.toLowerCase();
    return name.includes(q) || code.includes(q);
  };

  const filterChildrenAndCheckMatches = (cat: Category): { filteredChildren: Category[]; hasMatch: boolean } => {
    if (!cat.children?.length) {
      return { filteredChildren: [], hasMatch: matchesSearch(cat) };
    }

    const selfMatches = matchesSearch(cat);
    const childResults = cat.children.map((child) => filterChildrenAndCheckMatches(child));

    const matchingChildren = cat.children
      .map((child, index) => ({ child, index }))
      .filter(({ child, index }) => childResults[index].hasMatch || matchesSearch(child))
      .map(({ child, index }) => ({
        ...child,
        children: childResults[index].filteredChildren,
      }));

    return {
      filteredChildren: matchingChildren,
      hasMatch: selfMatches || matchingChildren.length > 0,
    };
  };

  return cats
    .map((cat) => {
      const result = filterChildrenAndCheckMatches(cat);
      if (result.hasMatch) {
        return {
          ...cat,
          children: result.filteredChildren,
        };
      }
      return null;
    })
    .filter((cat): cat is Category => cat !== null);
}

function TreeRow({
  cat,
  level,
  registerField,
  selectedId,
}: {
  cat: Category;
  level: number;
  registerField: UseFormRegisterReturn;
  selectedId: string | undefined;
}) {
  const { locale, isReady } = useRouter();

  if (!isReady) return null;

  const children = cat.children?.length ? cat.children : [];

  return (
    <div data-level={level} data-code={cat.code} className="w-full text-left">
      <FancyRadiobox
        value={cat.id}
        register={registerField}
        labelId={`single-cat-${cat.id}`}
        defaultChecked={selectedId === cat.id}
      >
        <Indent level={level} />
        <span data-single-category-id={cat.id}>{localeCore.getLocaleValueFallback(cat.name, locale!)}</span>
      </FancyRadiobox>
      {children.length > 0 && (
        <div className="space-y-1 mt-2">
          {children.map((child) => (
            <TreeRow
              key={child.id}
              cat={child}
              level={level + 1}
              registerField={registerField}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Indent({ level }: { level: number }) {
  const elements: JSX.Element[] = [];
  for (let i = 0; i < level; i++) {
    elements.push(<UpIcon key={i} className="w-4 h-4 shrink-0" />);
  }
  return <>{elements}</>;
}
